// Admin → Families: every family, its parents and children, and adding or managing an account.
import { ChevronRight, Crown, Eye, KeyRound, Laptop, LogOut, PauseCircle, Pencil, PlayCircle, Plus, Puzzle, RefreshCw, Settings2, Trash2, Unlock, UserPlus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, impersonation } from '../../lib/api';
import { clock, dayLabel, relative } from '../../lib/format';
import type { AdminChild, AdminFamily, AdminFamilyMember, AdminOverview, AdminParent, NotifyCategory, PermissionCatalog } from '../../lib/types';
import { NOTIFY_LABELS } from '../../lib/types';
import { useParent } from '../ParentContext';
import { FamilyPlanEditor } from '../Plans';
import { FamilyAmbassadorLine } from './AmbassadorFamilies';
import { FamilySignupCodeLine } from './SignupCodes';
import { FamilyRecordsLine } from './FamilyRecords';
import { LinkResult, Stat, TimeZoneSelect, dollars } from './shared';
import { MergeChoicesTable, type MergeChoices, type MergePreview } from '../MergeFamilies';
import { MemberPermissionsEditor } from '../MemberPermissions';

export function Families() {
  const { toast } = useParent();
  const [data, setData] = useState<AdminOverview | null>(null);
  const [filter, setFilter] = useState('');
  const [planFilter, setPlanFilter] = useState<string | null>(null); // a plan id, '' for no plan, null for all
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    api.get<AdminOverview>('/api/admin/overview').then(setData).catch((e) => toast({ text: e.message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);

  if (!data) return <p className="muted">Loading…</p>;

  const parentsById = new Map(data.parents.map((p) => [p.id, p]));
  const q = filter.trim().toLowerCase();
  const byPlan = planFilter === null ? data.families : data.families.filter((f) => (f.plan?.planId ?? '') === planFilter);
  const families = q
    ? byPlan.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          f.members.some((m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)) ||
          f.children.some((c) => c.name.toLowerCase().includes(q)),
      )
    : byPlan;

  return (
    <div className="stack">
      <div className="admin-stats">
        <Stat label="Families" value={data.totals.families} />
        <Stat label="Parents" value={data.totals.parents} />
        <Stat label="Students" value={data.totals.children} />
        <Stat label="Online now" value={data.totals.onlineDevices} />
      </div>
      <PlanChips families={data.families} selected={planFilter} onSelect={setPlanFilter} />
      <div className="row-between">
        <input className="grow" placeholder="Search families, parents, emails or students" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <button className="icon-btn" onClick={load} aria-label="Refresh">
          <RefreshCw size={18} />
        </button>
        <button className="btn primary small" onClick={() => setAdding((a) => !a)}>
          <UserPlus size={15} /> Add parent
        </button>
      </div>
      {adding && <AddParent onDone={load} onClose={() => setAdding(false)} />}
      {!families.length && <p className="muted">{q || planFilter !== null ? 'No matches.' : 'No families yet.'}</p>}
      {families.map((f) => (
        <FamilyGroup key={f.id} family={f} families={families} parentsById={parentsById} catalog={data.permissions} reload={load} />
      ))}
    </div>
  );
}
/** One family: its parents (each fully manageable), pending invites, add/remove members, and its students. */
function FamilyGroup({
  family,
  families,
  parentsById,
  catalog,
  reload,
}: {
  family: AdminFamily;
  families: AdminFamily[];
  parentsById: Map<string, AdminParent>;
  catalog?: PermissionCatalog;
  reload: () => void;
}) {
  const { toast } = useParent();
  const [expanded, setExpanded] = useState(false);
  const [managingStudents, setManagingStudents] = useState(false);
  const [email, setEmail] = useState('');
  const [link, setLink] = useState<{ url: string; emailed: boolean; email: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(run: () => Promise<unknown>, done: string) {
    try {
      await run();
      if (done) toast({ text: done, tone: 'good' });
      reload();
      return true;
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      return false;
    }
  }

  async function addMember() {
    setBusy(true);
    try {
      const r = await api.post<{ added?: boolean; invited?: boolean; url?: string; emailed?: boolean; name?: string }>(
        `/api/admin/families/${family.id}/members`,
        { email: email.trim() },
      );
      setEmail('');
      if (r.added) toast({ text: `Added ${r.name} to ${family.name}.`, tone: 'good' });
      else if (r.invited) setLink({ url: r.url!, emailed: !!r.emailed, email: email.trim() });
      reload();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  const deviceCount = family.children.reduce((n, c) => n + c.devices.length, 0);
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

  return (
    <section className="card stack admin-family-group">
      <button className="family-toggle grow" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} aria-label={`${family.name} — details`}>
        <ChevronRight size={18} className={`chevron ${expanded ? 'open' : ''}`} />
        <span className="grow">
          <span className="family-name">{family.name}</span>
          <span className="muted small block">
            {plural(family.members.length, 'parent')} · {plural(family.children.length, 'student')} · {plural(deviceCount, 'device')}
          </span>
        </span>
      </button>

      <FamilyPlanEditor family={family} onChanged={reload} />
      {family.ambassador && <FamilyAmbassadorLine familyId={family.id} a={family.ambassador} onChanged={reload} />}
      {family.signupCode && <FamilySignupCodeLine familyId={family.id} c={family.signupCode} onChanged={reload} />}
      {family.records && (family.records.deleteAt > 0 || family.records.deletedAt > 0) && <FamilyRecordsLine familyId={family.id} name={family.name} records={family.records} onChanged={reload} />}
      {!!family.plan?.renewalConsentAt && (
        <p className="muted small" title={family.plan.renewalConsentText}>
          Agreed to the renewal terms at checkout on {new Date(family.plan.renewalConsentAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
        </p>
      )}

      {expanded && (
        <div className="stack">
          <div className="field-label">Parents</div>
          {family.members.map((m) => {
            const p = parentsById.get(m.id);
            if (!p) return null;
            return (
              <div key={m.id} className="stack admin-member">
                <ParentCard
                  parent={p}
                  role={m.role}
                  reload={reload}
                  onRemove={
                    m.role === 'owner'
                      ? undefined
                      : () =>
                          confirm(`Remove ${m.name} from ${family.name}? They keep their account but lose access to this family's children.`) &&
                          act(() => api.del(`/api/admin/families/${family.id}/members/${m.id}`), `Removed ${m.name} from ${family.name}.`)
                  }
                />
                {m.role !== 'owner' && <MemberAccess family={family} member={m} catalog={catalog} act={act} reload={reload} />}
              </div>
            );
          })}
          {family.invites.map((i) => (
            <div key={i.email} className="row-between admin-invite">
              <span className="grow">
                {i.email} <span className="muted small">· invited</span>
              </span>
              <button className="btn small" onClick={() => act(() => api.post(`/api/admin/families/${family.id}/invite/cancel`, { email: i.email }), 'Invite cancelled.')}>
                Cancel
              </button>
            </div>
          ))}
          {link ? (
            <LinkResult url={link.url} emailed={link.emailed} to={link.email} what="invite link" />
          ) : (
            <div className="row-between">
              <input
                className="grow"
                type="email"
                placeholder="parent@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label={`Add a parent to ${family.name}`}
              />
              <button className="btn small primary" onClick={addMember} disabled={busy || !email.includes('@')}>
                <UserPlus size={15} /> Add parent
              </button>
            </div>
          )}

          <div className="row-between">
            <div className="field-label">Students</div>
            {!!family.children.length && (
              <button className="btn small" onClick={() => setManagingStudents((s) => !s)}>
                {managingStudents ? 'Done' : 'Manage students'}
              </button>
            )}
          </div>
          {!family.children.length && <p className="muted small">No students yet.</p>}
          {family.children.map((c) => (
            <ChildRow key={c.id} child={c} managing={managingStudents} act={act} />
          ))}
          <AdminMerge family={family} families={families} onDone={reload} />
        </div>
      )}
    </section>
  );
}
/**
 * Families by plan (#384): a chip per plan with its family count and expected monthly amount, which filters the
 * list. "Now" is what families pay today (free periods count as $0); "after free periods" is once trials end.
 */
function PlanChips({ families, selected, onSelect }: { families: AdminFamily[]; selected: string | null; onSelect: (id: string | null) => void }) {
  const groups = new Map<string, { name: string; count: number; now: number; regular: number }>();
  for (const f of families) {
    const id = f.plan?.planId ?? '';
    const g = groups.get(id) ?? { name: f.plan?.planName || 'No plan', count: 0, now: 0, regular: 0 };
    g.count++;
    g.now += f.plan?.monthlyCents ?? 0;
    g.regular += f.plan?.regularMonthlyCents ?? 0;
    groups.set(id, g);
  }
  const list = [...groups.entries()].sort((a, b) => (a[0] === '' ? 1 : b[0] === '' ? -1 : a[1].name.localeCompare(b[1].name)));
  const totalNow = list.reduce((n, [, g]) => n + g.now, 0);
  const totalRegular = list.reduce((n, [, g]) => n + g.regular, 0);
  if (list.length === 1 && list[0]![0] === '') return null; // no plans in use yet
  return (
    <div className="stack plan-chips-wrap">
      <div className="plan-chips" role="group" aria-label="Filter families by plan">
        <button className={`plan-chip ${selected === null ? 'active' : ''}`} onClick={() => onSelect(null)} aria-pressed={selected === null}>
          <span className="plan-chip-name">All plans</span>
          <span className="plan-chip-count">{families.length}</span>
          <span className="plan-chip-money">{dollars(totalNow)}/mo</span>
        </button>
        {list.map(([id, g]) => (
          <button key={id || 'none'} className={`plan-chip ${selected === id ? 'active' : ''}`} onClick={() => onSelect(selected === id ? null : id)} aria-pressed={selected === id}>
            <span className="plan-chip-name">{g.name}</span>
            <span className="plan-chip-count">{g.count}</span>
            <span className="plan-chip-money">
              {dollars(g.now)}/mo{g.regular !== g.now ? ` · ${dollars(g.regular)} after free periods` : ''}
            </span>
          </button>
        ))}
      </div>
      <p className="muted small">
        Expected monthly intake: <strong>{dollars(totalNow)}</strong> now
        {totalRegular !== totalNow ? (
          <>
            , <strong>{dollars(totalRegular)}</strong> once trials and free periods end
          </>
        ) : null}
        . Billing isn’t live yet, so these are amounts at each plan’s price.
      </p>
    </div>
  );
}
function AddParent({ onDone, onClose }: { onDone: () => void; onClose: () => void }) {
  const { parent: me, toast } = useParent();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [tz, setTz] = useState(me.timezone);
  const [how, setHow] = useState<'invite' | 'password'>('invite');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ inviteUrl: string; emailed: boolean; email: string } | null>(null);

  async function create() {
    setBusy(true);
    try {
      const r = await api.post<{ inviteUrl: string; emailed: boolean }>('/api/admin/parents', {
        name: name.trim(),
        email: email.trim(),
        timezone: tz,
        ...(how === 'password' ? { password } : {}),
      });
      onDone();
      if (r.inviteUrl) setResult({ ...r, email: email.trim() });
      else {
        toast({ text: `Created ${name.trim()}’s account.`, tone: 'good' });
        onClose();
      }
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack">
      <h2>Add a parent</h2>
      {result ? (
        <>
          <p>Account created.</p>
          <LinkResult url={result.inviteUrl} emailed={result.emailed} to={result.email} what="invite link" />
          <div>
            <button className="btn small" onClick={onClose}>
              Done
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="form-row">
            <label className="grow">
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
            </label>
            <label className="grow">
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
          </div>
          <label>
            Time zone for school hours
            <TimeZoneSelect value={tz} onChange={setTz} />
          </label>
          <div className="segmented" role="radiogroup" aria-label="Password">
            <button role="radio" aria-checked={how === 'invite'} className={how === 'invite' ? 'active' : ''} onClick={() => setHow('invite')}>
              Send an invite link
            </button>
            <button role="radio" aria-checked={how === 'password'} className={how === 'password' ? 'active' : ''} onClick={() => setHow('password')}>
              Set a password now
            </button>
          </div>
          {how === 'invite' ? (
            <p className="muted small">They choose their own password from the link. It’s emailed when email is set up; you can always copy it.</p>
          ) : (
            <label>
              Password <span className="muted small">(at least 8 characters; share it with them safely)</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            </label>
          )}
          <div className="actions">
            <button
              className="btn primary small"
              onClick={create}
              disabled={busy || !name.trim() || !email.includes('@') || (how === 'password' && password.length < 8)}
            >
              {busy ? 'Creating…' : 'Create account'}
            </button>
            <button className="btn small" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        </>
      )}
    </section>
  );
}
function ParentCard({ parent, role, reload, onRemove }: { parent: AdminParent; role: string; reload: () => void; onRemove?: () => void }) {
  const { parent: me, toast } = useParent();
  const isMe = parent.email.toLowerCase() === me.email.toLowerCase();
  // One open/close state: the chevron and the Manage button both reveal this parent's details panel.
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(parent.name);
  const [email, setEmail] = useState(parent.email);
  const [tz, setTz] = useState(parent.timezone);
  const [newPassword, setNewPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [resetLink, setResetLink] = useState<{ url: string; emailed: boolean } | null>(null);
  const [newChild, setNewChild] = useState('');
  const protectedWhy = isMe ? 'This is your own account.' : parent.locked ? 'Set by ADMIN_EMAILS on the server.' : '';

  /** Run an admin action, then refresh the list. */
  async function act(run: () => Promise<unknown>, done: string) {
    try {
      await run();
      toast({ text: done, tone: 'good' });
      reload();
      return true;
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      return false;
    }
  }

  async function viewAs() {
    if (!confirm(`View ClassGuardian as ${parent.name} (${parent.email})? This is recorded in the audit log.`)) return;
    try {
      const r = await api.post<{ token: string; parent: { name: string } }>(`/api/admin/impersonate/${parent.id}`);
      impersonation.start(r.token, r.parent.name);
      location.assign('/');
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  const detailsDirty = name.trim() !== parent.name || email.trim().toLowerCase() !== parent.email || tz !== parent.timezone;

  return (
    <section className={`card stack admin-family ${parent.disabled ? 'suspended' : ''}`}>
      <div className="row-between family-head">
        <button className="family-toggle grow" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={`${parent.name} — details`}>
          <ChevronRight size={18} className={`chevron ${open ? 'open' : ''}`} />
          <span className="grow">
            <span className="family-name">
              {parent.name}
              {role === 'owner' && (
                <span title="Family owner">
                  <Crown size={14} className="owner-icon" />
                </span>
              )}
              {parent.isAdmin && <span className="tag feature">Admin</span>}
              {parent.ambassador && <span className="tag good">Brand ambassador</span>}
              {parent.disabled && <span className="tag bug">Suspended</span>}
              {parent.signin?.locked && <span className="tag bug">Locked</span>}
              {!parent.signin?.locked && !!parent.signin?.cooldownUntil && <span className="tag">Cooling down</span>}
              {parent.mfaEnabled && <span className="tag good">2FA</span>}
              {!!parent.deleteAt && <span className="tag bug">Deleting {dayLabel(parent.deleteAt)}</span>}
            </span>
            <span className="muted small block">
              {parent.email}
              {role === 'owner' ? ' · owner' : ''}
              {parent.signIns && (
                <span className="block" title={parent.signIns.lastAt ? `Last signed in ${dayLabel(parent.signIns.lastAt)} ${clock(parent.signIns.lastAt)}` : undefined}>
                  Signed in {parent.signIns.count} {parent.signIns.count === 1 ? 'time' : 'times'}
                  {parent.signIns.lastAt ? ` · last ${relative(parent.signIns.lastAt)}` : ''}
                </span>
              )}
            </span>
          </span>
        </button>
        <div className="actions">
          {!!parent.deleteAt && (
            <button
              className="btn small"
              onClick={() =>
                confirm(`Keep ${parent.name}’s account? Their deletion request is cancelled.`) &&
                api
                  .post(`/api/admin/parents/${parent.id}/cancel-deletion`)
                  .then(() => {
                    toast({ text: `${parent.name}’s account will be kept.`, tone: 'good' });
                    reload();
                  })
                  .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }))
              }
            >
              Keep account
            </button>
          )}
          {(parent.signin?.locked || !!parent.signin?.cooldownUntil) && (
            <button
              className="btn small"
              onClick={() => act(() => api.post(`/api/admin/parents/${parent.id}/unlock`), `${parent.name} unlocked.`)}
              title={`${parent.signin?.failed ?? 0} failed sign-ins in a row${parent.signin?.lastFailedIp ? `, last from ${parent.signin.lastFailedIp}` : ''}`}
            >
              <Unlock size={15} /> Unlock
            </button>
          )}
          {onRemove && (
            <button className="btn small danger-text" onClick={onRemove} title="Remove from this family">
              <X size={15} /> Remove
            </button>
          )}
          {!isMe && !parent.disabled && (
            <button className="btn small" onClick={viewAs}>
              <Eye size={15} /> View as
            </button>
          )}
          <button className={`btn small ${open ? 'active' : ''}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            <Settings2 size={15} /> {open ? 'Close' : 'Manage'}
          </button>
        </div>
      </div>

      {open && (
        <div className="admin-manage stack">
          <div className="form-row">
            <label className="grow">
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
            </label>
            <label className="grow">
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={parent.locked} />
            </label>
          </div>
          <label>
            Time zone
            <TimeZoneSelect value={tz} onChange={setTz} />
          </label>
          <div>
            <button
              className="btn small primary"
              disabled={!detailsDirty || !name.trim()}
              onClick={() =>
                act(
                  () => api.patch(`/api/admin/parents/${parent.id}`, { name: name.trim(), email: email.trim(), timezone: tz }),
                  'Saved.',
                )
              }
            >
              Save details
            </button>
          </div>

          <div className="field-label">Password and sign-in</div>
          <div className="actions">
            <button
              className="btn small"
              onClick={async () => {
                try {
                  setResetLink(await api.post<{ url: string; emailed: boolean }>(`/api/admin/parents/${parent.id}/reset-link`));
                } catch (e) {
                  toast({ text: (e as Error).message, tone: 'alert' });
                }
              }}
            >
              <KeyRound size={15} /> Send a reset link
            </button>
            <button className="btn small" onClick={() => setSettingPassword((s) => !s)}>
              Set a password
            </button>
            {!isMe && (
              <button
                className="btn small"
                onClick={() => act(() => api.post(`/api/admin/parents/${parent.id}/signout`), `${parent.name} is signed out everywhere.`)}
              >
                <LogOut size={15} /> Sign out everywhere
              </button>
            )}
          </div>
          {resetLink && <LinkResult url={resetLink.url} emailed={resetLink.emailed} to={parent.email} what="reset link" />}
          {settingPassword && (
            <div className="row-between">
              <input
                className="grow"
                type="password"
                placeholder="New password (at least 8 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                aria-label="New password"
              />
              <button
                className="btn small primary"
                disabled={newPassword.length < 8}
                onClick={async () => {
                  if (await act(() => api.post(`/api/admin/parents/${parent.id}/password`, { password: newPassword }), 'Password changed. They’ve been signed out elsewhere.')) {
                    setNewPassword('');
                    setSettingPassword(false);
                  }
                }}
              >
                Save password
              </button>
            </div>
          )}

          <div className="field-label">Two-factor</div>
          <label className="check">
            <input
              type="checkbox"
              checked={parent.mfaRequired}
              onChange={(e) => act(() => api.patch(`/api/admin/parents/${parent.id}`, { mfaRequired: e.target.checked }), e.target.checked ? 'Two-factor required for this account.' : 'No longer required.')}
            />
            <span>
              Require two-factor for this account
              <span className="muted small block">They’ll have to set it up before they can use the dashboard.</span>
            </span>
          </label>
          <div className="actions">
            <span className="muted small">{parent.mfaEnabled ? 'Two-factor is set up.' : 'Not set up.'}</span>
            {parent.mfaEnabled && (
              <button
                className="btn small"
                onClick={() =>
                  confirm(`Reset ${parent.name}’s two-factor? They’ll be signed out and can set it up again (use this if they lost their phone).`) &&
                  act(() => api.post(`/api/admin/parents/${parent.id}/reset-mfa`), 'Two-factor reset.')
                }
              >
                Reset two-factor
              </button>
            )}
          </div>

          <div className="field-label">Notifications</div>
          <p className="muted small">Which events email {parent.name}.</p>
          {(Object.keys(NOTIFY_LABELS) as NotifyCategory[]).map((cat) => (
            <label key={cat} className="check">
              <input
                type="checkbox"
                checked={!!parent.notifyPrefs[cat]}
                onChange={(e) =>
                  act(
                    () => api.patch(`/api/admin/parents/${parent.id}`, { notifyPrefs: { ...parent.notifyPrefs, [cat]: e.target.checked } }),
                    `Updated ${parent.name}’s notifications.`,
                  )
                }
              />
              <span>{NOTIFY_LABELS[cat]}</span>
            </label>
          ))}

          <div className="field-label">Students</div>
          <div className="row-between">
            <input className="grow" placeholder="Student’s name" value={newChild} onChange={(e) => setNewChild(e.target.value)} maxLength={60} aria-label="New student’s name" />
            <button
              className="btn small"
              disabled={!newChild.trim()}
              onClick={async () => {
                if (await act(() => api.post(`/api/admin/parents/${parent.id}/children`, { name: newChild.trim() }), `Added ${newChild.trim()}.`)) setNewChild('');
              }}
            >
              <Plus size={15} /> Add student
            </button>
          </div>

          <div className="field-label">Account</div>
          <div className="actions">
            <button
              className="btn small"
              disabled={!!protectedWhy}
              title={protectedWhy}
              onClick={() =>
                act(
                  () => api.patch(`/api/admin/parents/${parent.id}`, { disabled: !parent.disabled }),
                  parent.disabled ? `${parent.name} can sign in again.` : `${parent.name} is suspended and signed out.`,
                )
              }
            >
              {parent.disabled ? <PlayCircle size={15} /> : <PauseCircle size={15} />} {parent.disabled ? 'Reactivate' : 'Suspend'}
            </button>
            <button
              className="btn small danger-text"
              disabled={!!protectedWhy}
              title={protectedWhy}
              onClick={() => {
                if (
                  confirm(
                    `Delete ${parent.name} (${parent.email})?\n\nThis removes their account, every student, paired device, schedule and all history. It can’t be undone.`,
                  )
                )
                  act(() => api.del(`/api/admin/parents/${parent.id}`), `Deleted ${parent.name}’s account.`);
              }}
            >
              <Trash2 size={15} /> Delete account
            </button>
          </div>
          {protectedWhy && <p className="muted small">{protectedWhy} It can’t be suspended or deleted here.</p>}
        </div>
      )}

      {open && (
        <p className="muted small">
          {parent.termsVersion
            ? `Accepted Terms version ${parent.termsVersion} ${dayLabel(parent.termsAcceptedAt)}${parent.termsIp ? ` · ${parent.termsIp}` : ''}`
            : 'Hasn’t accepted the Terms yet'}
        </p>
      )}
    </section>
  );
}
function ChildRow({
  child,
  managing,
  act,
}: {
  child: AdminChild;
  managing: boolean;
  act: (run: () => Promise<unknown>, done: string) => Promise<boolean>;
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(child.name);
  return (
    <div className="admin-child">
      <div className="row-between">
        {renaming ? (
          <input className="grow" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} aria-label="Student’s name" />
        ) : (
          <div className="admin-child-name grow">
            {child.name}
            {child.studentLocked && <span className="tag bug">Student Portal locked</span>}
            {!child.studentLocked && child.studentCooldown && <span className="tag">Student Portal cooling down</span>}
          </div>
        )}
        {(child.studentLocked || child.studentCooldown) && !renaming && (
          <button className="btn small" onClick={() => act(() => api.post(`/api/admin/children/${child.id}/unlock-student`), `${child.name}’s Student Portal unlocked.`)}>
            <Unlock size={14} /> Unlock
          </button>
        )}
        {managing &&
          (renaming ? (
            <>
              <button
                className="btn small primary"
                disabled={!name.trim()}
                onClick={async () => {
                  if (await act(() => api.patch(`/api/admin/children/${child.id}`, { name: name.trim() }), 'Renamed.')) setRenaming(false);
                }}
              >
                Save
              </button>
              <button className="btn small" onClick={() => setRenaming(false)}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button className="icon-btn" onClick={() => setRenaming(true)} aria-label={`Rename ${child.name}`}>
                <Pencil size={16} />
              </button>
              <button
                className="icon-btn"
                aria-label={`Remove ${child.name}`}
                onClick={() => {
                  if (confirm(`Remove ${child.name}? This deletes their schedule, rules, activity and paired devices.`))
                    act(() => api.del(`/api/admin/children/${child.id}`), `Removed ${child.name}.`);
                }}
              >
                <Trash2 size={16} />
              </button>
            </>
          ))}
      </div>
      {!child.devices.length && <div className="muted small">Not paired to any device.</div>}
      <ul className="admin-devices">
        {child.devices.map((d) => (
          <li key={d.id}>
            {d.kind === 'agent' ? <Laptop size={15} /> : <Puzzle size={15} />}
            <span className="grow">
              {d.name}
              <span className="muted small">
                {' '}
                · {d.kind === 'agent' ? 'App' : 'Extension'}
                {d.appVersion && ` v${d.appVersion}`}
                {d.platform && ` · ${d.platform}`}
              </span>
            </span>
            <span className={`admin-presence ${d.online ? 'on' : ''}`}>
              {d.online ? 'online' : d.lastSeenAt ? `seen ${relative(d.lastSeenAt)}` : 'never seen'}
            </span>
            {managing && (
              <button
                className="icon-btn"
                aria-label={`Unpair ${d.name}`}
                onClick={() => {
                  if (confirm(`Unpair ${d.name}? It will need a new code to pair again.`)) act(() => api.del(`/api/admin/devices/${d.id}`), `Unpaired ${d.name}.`);
                }}
              >
                <X size={16} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Merge this family into another one for a customer (#509): the same side-by-side the owner gets. Audited. */
function AdminMerge({ family, families, onDone }: { family: AdminFamily; families: AdminFamily[]; onDone: () => void }) {
  const { toast } = useParent();
  const [into, setInto] = useState('');
  const [preview, setPreview] = useState<(MergePreview & { refusal: string | null }) | null>(null);
  const [choices, setChoices] = useState<MergeChoices>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setPreview(null);
    setChoices({});
    if (!into) return;
    api
      .get<MergePreview & { refusal: string | null }>(`/api/admin/families/${family.id}/merge-preview?into=${encodeURIComponent(into)}`)
      .then(setPreview)
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [into, family.id, toast]);
  async function merge() {
    if (!preview || !confirm(`Merge ${preview.from.name} into ${preview.into.name}? This can’t be undone.`)) return;
    setBusy(true);
    try {
      await api.post('/api/admin/families/merge', { fromId: family.id, intoId: into, choices });
      toast({ text: `${preview.from.name} merged into ${preview.into.name}.`, tone: 'good' });
      setInto('');
      onDone();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  const others = families.filter((f) => f.id !== family.id);
  if (!others.length) return null;
  return (
    <div className="stack admin-merge">
      <label className="row-gap wrap">
        <span className="field-label">Merge into another family</span>
        <select value={into} onChange={(e) => setInto(e.target.value)} aria-label={`Merge ${family.name} into`}>
          <option value="">Choose a family…</option>
          {others.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </label>
      {preview && (
        <>
          <MergeChoicesTable preview={preview} choices={choices} onChange={setChoices} />
          {preview.refusal && <p className="error small">{preview.refusal}</p>}
          <div>
            <button className="btn danger-text" onClick={merge} disabled={busy || !!preview.refusal}>
              {busy ? 'Merging…' : `Merge into ${preview.into.name}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A family member's access, managed by an admin (#514): which students they see, what they can do, and handing them
 * the family. The same as the owner's Family and Sharing; each change is in the audit log.
 */
function MemberAccess({
  family,
  member,
  catalog,
  act,
  reload,
}: {
  family: AdminFamily;
  member: AdminFamilyMember;
  catalog?: PermissionCatalog;
  act: (run: () => Promise<unknown>, done: string) => Promise<boolean>;
  reload: () => void;
}) {
  const sees = member.childIds ?? [];
  const toggle = (childId: string, on: boolean) => {
    const next = on ? [...new Set([...sees, childId])] : sees.filter((id) => id !== childId);
    void act(() => api.put(`/api/family/${family.id}/members/${member.id}/access`, { childIds: next }), '');
  };
  return (
    <div className="stack admin-member-access">
      {!!family.children.length && (
        <div className="member-access">
          <span className="muted small">Can see:</span>
          {family.children.map((c) => (
            <label key={c.id} className="access-chip">
              <input type="checkbox" checked={sees.includes(c.id)} onChange={(e) => toggle(c.id, e.target.checked)} />
              <span>{c.name}</span>
            </label>
          ))}
        </div>
      )}
      {catalog && <MemberPermissionsEditor familyId={family.id} member={member} catalog={catalog} onSaved={reload} />}
      <div>
        <button
          className="btn small"
          onClick={() =>
            confirm(`Make ${member.name} the owner of ${family.name}? The current owner stays in it as a Co-parent.`) &&
            act(() => api.put(`/api/family/${family.id}/owner`, { parentId: member.id }), `${member.name} now owns ${family.name}.`)
          }
        >
          <Crown size={14} /> Make owner
        </button>
      </div>
    </div>
  );
}
